"use client"
import { useEffect, useRef } from "react"
import { useTransitionRouter } from "next-view-transitions"
import { IconBrandLinkedin, IconCopyright } from "@tabler/icons-react"
import gsap from "gsap"
import { ScrollTrigger } from "gsap/ScrollTrigger"
import { SplitText } from "gsap/SplitText"
import { slideInOut } from "@/app/lib/gsapAnimation"
import Image from "next/image"
import { usePathname } from "next/navigation"
import TextSplit from "../ui/TextSplit"

gsap.registerPlugin(SplitText, ScrollTrigger)

export default function Footer() {

  const transitionRouter = useTransitionRouter()
  const pathname = usePathname()

  const mainFooterRef = useRef(null)

  const handleClick = (e: React.MouseEvent, url: string) => {
    e.preventDefault()
  
    // Blur the currently focused element
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur()
    }
    
    transitionRouter.push(url, { onTransitionReady: slideInOut })
  }


  useEffect(() => {
    const footer = mainFooterRef.current
    const gototop = footer?.querySelector(".goto-top")
    const navItems = footer?.querySelectorAll(".footer-nav button span")

    // Create split text
    const splitGototop = new SplitText(gototop, { type: "words", autoSplit: true })
    const splitNavItems = new SplitText(navItems, { type: "chars, lines", autoSplit: true, aria: "none" })

    // Animation
    gsap.from(splitGototop.words, {
      y: 100,
      autoAlpha: 0,
      stagger: {
        amount: 0.5
      },
      ease: "power3.out",
      scrollTrigger: {
        trigger: footer,
        start: "bottom-=200px bottom"
      }
    })

    gsap.from(splitNavItems.chars, {
      y: 100,
      autoAlpha: 0,
      stagger: {
        amount: 0.5,
        from: "random"
      },
      ease: "power3.out",
      mask: "lines",
      scrollTrigger: {
        trigger: footer,
        start: "bottom-=50px bottom"
      }
    })

    // Cleanup to prevent memory leaks
    return () => {
      splitGototop.revert()
      splitNavItems.revert()
    }
  }, [pathname]) 

  return (
    <footer ref={mainFooterRef} className="bg-[#163b35] text-white">
      <div className="relative md:min-h-[840px] lg:min-h-[640px]" style={{ clipPath: "polygon(0% 0%, 100% 0%, 100% 100%, 0 100%)" }}>
        <div className="relative md:fixed bottom-0 md:min-h-[640px] lg:min-h-[500px] w-full max-h-full">
          <div className="container flex flex-col justify-between h-full py-6 md:gap-y-1">
            {/* Row */}
            <div className="relative grid grid-cols-12 max-w-full mt-12 mb-12">
              <div className="col-span-9 flex flex-col md:flex-row gap-x-12 gap-y-5 w-full">
                <figure className="relative max-w-32 md:max-w-44 h-auto">
                  <img src="/logo/makingyousmile-white.svg" alt="logo Making You Smile white color" width={150} height="90" />
                </figure>
                <TextSplit animateOnScroll={true} delay={0.75}>
                  <div className="text-base leading-[1.75] mt-2 pb-4">
                    Small agency. <br />
                    Big branding power. <br />
                    Multilingual edge.
                  </div>
                </TextSplit>
              </div>
              <div className="relative col-span-3">
                <div className="absolute right-0 md:right-4 sm:relative sm:top-0 mt-2 flex justify-end" onClick={() => scrollTo(0, 0)}>
                  <button className="flex items-center border rounded-full p-2 w-fit h-fit goto-top gap-x-2">⇡ To the top</button>
                </div>
              </div>
            </div>

            {/* Row */}
            <div className="grid grid-cols-12">
              <div className="col-span-12 sm:col-span-6 xl:col-span-5">
                <div className="grid grid-cols-1 sm:grid-cols-2 text-base md:text-lg pb-12">
                  <div></div>
                  <div className="footer-nav">
                    <div className="py-2 uppercase hover:text-[#FF3C5C]">
                      <button onClick={e => handleClick(e, "/")}>
                        <span>Home</span>
                      </button>
                    </div>
                    <div className="py-2 uppercase hover:text-[#FF3C5C]">
                      <button onClick={e => handleClick(e, "/work")}>
                        <span>Work</span>
                      </button>
                    </div>
                    <div className="py-2 uppercase hover:text-[#FF3C5C]">
                      <button onClick={e => handleClick(e, "/insights")}>
                        <span>Insights</span>
                      </button>
                    </div>
                    <div className="py-2 uppercase hover:text-[#FF3C5C]">
                      <button onClick={e => handleClick(e, "/about")}>
                        <span>About</span>
                      </button>
                    </div>
                    <div className="pt-3 md:pt-1">
                      <a href="https://fr.linkedin.com/company/makingyousmile" target="_blank">
                        <IconBrandLinkedin size={32} className="hover:scale-[2.5] hover:text-[#FF3C5C] origin-center duration-200 ease-in-out transition-transform" />
                      </a>
                    </div>
                  </div>
                </div>
              </div>

              <div className="col-span-12 sm:col-span-6 xl:col-span-7">
                <div className="flex flex-col gap-3 max-w-[460px] sm:px-4">
                  <div className="pt-2">
                    <label htmlFor="newsletter">Join the circle</label>
                  </div>
                  <div className="relative flex" onClick={e => handleClick(e, "/mysinsider")}>
                    <input type="email" name="newsletter" className="rounded-full w-full py-2 px-4" placeholder="Email address" />
                    <input type="submit" value="Join" className="absolute right-0 py-2 px-6 rounded-full bg-[#FF3C5C] inline-block -ml-10" />
                  </div>
                  <span className="text-sm leading-[1.6]">Sign up for exclusive content and MYS insiders-only announcements. Find fresh ideas, inspiring stories, and reasons to smile in your inbox.</span>
                </div>
              </div>
            </div>

            {/* Row */}
            <div className="flex flex-col gap-y-4 md:flex-row justify-between items-center py-12 text-sm">
              <div className="flex flex-1 items-center">
                <IconCopyright size={16} />
                <span className="">Making You Smile</span>
              </div>
              <div className="flex flex-1 w-full text-center gap-x-2 justify-center">
                <span className="underline">hi@makingyousmile.fr</span> | 06 73 38 07 75
              </div>
              <div className="flex-1 text-end">
                <button onClick={e => handleClick(e, "/legal-notice")}>Legal notice</button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  )
}
